import { useCallback } from "react"
import { useSelector } from "react-redux"

import type { SpanPlotReqConfig } from "types/dashboard.types"
import type { DateRange } from "types/date.types"
import type { TelemetryFetcher, QualityFetcher } from "types/telemetries.types"

import { useLazySpanTelemetryQuery, useLazySpanTelemetryQualityQuery } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import {
  useMultiEntityTelemetry,
  type UseMultiEntityTelemetryReturn,
} from "./useMultiEntityTelemetry"

interface UseMultiSpanTelemetryProps {
  spanIds: number[]
  reqConfig: SpanPlotReqConfig
  dateRange: DateRange | null
  isDateRangeValid: boolean
}

export const useMultiSpanTelemetry = ({
  spanIds,
  reqConfig,
  dateRange,
  isDateRangeValid,
}: UseMultiSpanTelemetryProps): UseMultiEntityTelemetryReturn => {
  const orgId = useSelector(selectCurrentOrgId)
  const [fetchSpanTelemetry] = useLazySpanTelemetryQuery()
  const [fetchSpanTelemetryQuality] = useLazySpanTelemetryQualityQuery()
  const { measurement } = reqConfig

  const fetchTelemetry = useCallback<TelemetryFetcher>(
    (id, params, preferCacheValue) =>
      fetchSpanTelemetry(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        preferCacheValue,
      ).unwrap(),
    [fetchSpanTelemetry, orgId, measurement],
  )

  const fetchTelemetryQuality = useCallback<QualityFetcher>(
    (id, params) =>
      fetchSpanTelemetryQuality(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        true,
      ).unwrap(),
    [fetchSpanTelemetryQuality, orgId, measurement],
  )

  return useMultiEntityTelemetry({
    ids: spanIds,
    reqConfig,
    dateRange,
    isDateRangeValid,
    enabled: orgId !== undefined,
    fetchTelemetry,
    fetchTelemetryQuality,
  })
}
